import './perks-post-submission.component.scss';

import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import { HttpError } from '../../../api/http-error';
import { IconCloseCircle } from '../../../common/icons/icon-close-circle';
import { Pageable } from '../../../common/page/pageable.model';
import { useFeatureFlag } from '../../../feature-flags/hooks/use-feature-flag.hook';
import { FeatureFlagName } from '../../../feature-flags/models/feature-flag-names.enum';
import { useTranslation } from '../../../i18n';
import { useAppDispatch } from '../../../store/hooks';
import { toastService } from '../../../toast/toast.service';
import { buttonize } from '../../../utils/a11y.utils';
import type { User } from '../../model/user.interface';
import { PerksHelper } from '../../perks.helper';
import { PerksService } from '../../perks.services';
import { loadPosts } from '../../store/perks.thunk';
import { PerksBadge } from '../perks-badge/perks-badge.component';
import { usePostFilterBar } from '../perks-posts/post-filter-bar.context';

const MAX_IMAGE_BYTES = 25 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif'];
const MAX_TITLE_LENGTH = 255;
const MAX_CAPTION_LENGTH = 4096;

const perksService = new PerksService();

const submitErrorKey = (error: unknown): string => {
  if (error instanceof HttpError) {
    if (error.status === 429) {
      return 'perks.submission.errorLimit';
    }
    if (error.status === 409) {
      return 'perks.submission.errorUsername';
    }
  }
  return 'perks.submission.errorSubmit';
};

interface Props {
  /**
   * Called, instead of showing the confirmation panel, when the post went live at once -- an
   * author or moderator whose post a gate covers. The host takes it from there: the feed returns
   * to itself and scrolls to the post (SNGL-7344). Left out, as in the standalone submission block,
   * the confirmation panel stays, since there is no feed to go back to.
   */
  onPublished?: (postId: string) => void;
}

export const PerksPostSubmission = ({ onPublished }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // the feed's active filter, so the reload after publishing keeps what the filter bar shows
  const filterBar = usePostFilterBar();
  const [user, setUser] = useState<User | null>(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [title, setTitle] = useState('');
  const [caption, setCaption] = useState('');
  const [imageFileSetId, setImageFileSetId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // state of the created post: PUBLISHED when an author/moderator post was auto-published
  const [submittedState, setSubmittedState] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    perksService
      .getUser()
      .then((loaded) => setUser(loaded))
      .catch(() => setUser(null))
      .finally(() => setUserLoaded(true));
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = async (file: File | undefined) => {
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toastService.error(t('common.error'), t('perks.submission.invalidImageType'));
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toastService.error(t('common.error'), t('perks.submission.invalidImageSize'));
      return;
    }
    const filename = file.name.replace(/[^a-zA-Z0-9.]/g, '-');
    setIsUploading(true);
    try {
      const response = await perksService.uploadAvatar(file, filename);
      setImageFileSetId(response.imageFileSetId ?? null);
      setPreviewUrl(URL.createObjectURL(file));
    } catch {
      toastService.error(t('common.error'), t('perks.submission.errorUploadImage'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = (e: MouseEvent) => {
    e.stopPropagation();
    setImageFileSetId(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // artists and moderators skip the review queue, so their copy says Post, not Submit -- once the store
  // has community-official-authors on; with it off everyone is a fan submitter
  const officialAuthorsEnabled = useFeatureFlag(FeatureFlagName.COMMUNITY_OFFICIAL_AUTHORS);
  const skipsReview = officialAuthorsEnabled && (!!user?.officialAuthor || !!user?.moderator);
  // the image is optional; a submission without one becomes a text post
  const canSubmit = !!title.trim() && !!caption.trim() && !isUploading && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const response = await perksService.createSubmission({
        title: title.trim(),
        textContent: caption.trim(),
        imageFileSetId: imageFileSetId ?? undefined,
      });
      const state = response?.state ?? 'DRAFT';
      if (state === 'PUBLISHED' && onPublished && response?.postId) {
        // the host unmounts this form; nothing here should render again
        onPublished(response.postId);
        return;
      }
      setSubmittedState(state);
      if (state === 'PUBLISHED') {
        // the feed is held in the store; pull it again so the new post is there when they go back
        dispatch(
          loadPosts({
            pageable: Pageable.of(
              0,
              PerksHelper.POSTS_DEFAULT_PAGE_SIZE,
              PerksHelper.POSTS_DEFAULT_SORT
            ),
            filter: filterBar.filter,
          })
        );
      }
    } catch (e) {
      toastService.error(t('common.error'), t(submitErrorKey(e)));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setTitle('');
    setCaption('');
    setImageFileSetId(null);
    setPreviewUrl(null);
    setSubmittedState(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!userLoaded) {
    return <div class="single-post-submission single-post-submission-loading" />;
  }

  if (!user?.type) {
    return (
      <div class="single-post-submission">
        <p class="single-post-submission-login">{t('perks.submission.loginRequired')}</p>
      </div>
    );
  }

  if (submittedState) {
    return (
      <div class="single-post-submission">
        <div class="single-post-submission-success">
          <h4>
            {t(
              submittedState === 'PUBLISHED'
                ? 'perks.submission.successPublished'
                : 'perks.submission.success'
            )}
          </h4>
          <button class="single-post-submission-button" onClick={handleReset}>
            {t(skipsReview ? 'perks.submission.postAnother' : 'perks.submission.submitAnother')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div class="single-post-submission">
      <p class="single-post-submission-user">
        {t('perks.submission.postingAs')}{' '}
        <strong>
          {(officialAuthorsEnabled && user.officialAuthor?.name) ||
            user.username ||
            `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()}
        </strong>
        {user.officialAuthor && <PerksBadge kind="artist" />}
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.gif"
        hidden
        style="display: none !important"
        onChange={(e) => handleFileChange((e.target as HTMLInputElement).files?.[0])}
      />

      <div class="single-post-submission-image-wrap">
        <div
          class={`single-post-submission-image ${previewUrl ? 'has-preview' : ''}`}
          {...buttonize(
            () => !isUploading && fileInputRef.current?.click(),
            t(previewUrl ? 'perks.submission.changeImage' : 'perks.submission.addImage')
          )}
        >
          {previewUrl ? (
            <img src={previewUrl} alt={t('perks.submission.previewAlt')} />
          ) : (
            <span>
              {isUploading ? t('perks.submission.uploading') : t('perks.submission.addImage')}
            </span>
          )}
        </div>
        {previewUrl && !isUploading && (
          <button
            type="button"
            class="single-post-submission-remove"
            aria-label={t('perks.submission.removeImage')}
            title={t('perks.submission.removeImage')}
            onClick={handleRemoveImage}
          >
            <IconCloseCircle />
          </button>
        )}
      </div>

      <label class="single-post-submission-label" htmlFor="single-post-submission-title">
        {t('perks.submission.titleLabel')}
      </label>
      <input
        id="single-post-submission-title"
        class="single-post-submission-title"
        type="text"
        maxLength={MAX_TITLE_LENGTH}
        placeholder={t('perks.submission.titlePlaceholder')}
        value={title}
        onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
      />

      <label class="single-post-submission-label" htmlFor="single-post-submission-caption">
        {t('perks.submission.captionLabel')}
      </label>
      <textarea
        id="single-post-submission-caption"
        class="single-post-submission-caption"
        maxLength={MAX_CAPTION_LENGTH}
        placeholder={t('perks.submission.captionPlaceholder')}
        value={caption}
        onInput={(e) => setCaption((e.target as HTMLTextAreaElement).value)}
      />

      <div class="single-post-submission-actions">
        <button class="single-post-submission-button" disabled={!canSubmit} onClick={handleSubmit}>
          {isSubmitting
            ? t(skipsReview ? 'perks.submission.posting' : 'perks.submission.submitting')
            : t(skipsReview ? 'perks.submission.post' : 'perks.submission.submit')}
        </button>
      </div>
    </div>
  );
};
